<template>
  <div class="sidebar-wrapper">
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        :default-active="$route.path"
        background-color="#304156"
        text-color="#fff"
        active-text-color="#ffd04b"
        mode="vertical"
        :collapse="isCollapse"
        :router="true"
      >
        <!-- 只有当分组里至少有一个可见子项时，才渲染该分组 -->
        <el-submenu
          v-for="(group, gi) in visibleMenuGroups"
          :key="gi"
          :index="String(gi)"
        >
          <template #title>
            <i :class="group.icon"></i>
            <span>{{ group.title }}</span>
          </template>
          <el-menu-item-group>
            <el-menu-item
              v-for="item in group.items"
              :key="item.path"
              :index="item.path"
            >
              <span class="menu-item-label">
                <span>{{ item.label }}</span>
                <span
                  v-if="item.path === '/sounds/findSound' && findSoundUnreadQueryCount > 0"
                  class="menu-item-unread-dot"
                ></span>
              </span>
            </el-menu-item>
          </el-menu-item-group>
        </el-submenu>
      </el-menu>
    </el-scrollbar>

    <div class="sidebar-footer" :class="{ collapsed: isCollapse }">
      <el-tooltip content="系统设置" placement="right" :disabled="!isCollapse">
        <button type="button" class="sidebar-settings-button" @click="openLocalSettings">
          <i class="el-icon-setting"></i>
          <span v-if="!isCollapse">系统设置</span>
        </button>
      </el-tooltip>
    </div>

    <el-dialog
      title="系统设置"
      :visible.sync="localSettingsVisible"
      width="560px"
      append-to-body
      :close-on-click-modal="false"
    >
      <section class="local-setting-section">
        <div class="setting-heading">
          <div>
            <h3>默认标签整理方式</h3>
            <p>整理音效时仍可随时在引导模式和快速模式之间切换。</p>
          </div>
          <el-tag size="mini" type="info">仅保存在本机</el-tag>
        </div>

        <el-radio-group v-model="localSettings.defaultEditorMode" class="mode-setting-grid">
          <el-radio label="guided" class="mode-setting-card">
            <span class="mode-setting-icon"><i class="el-icon-guide"></i></span>
            <span>
              <strong>引导模式</strong>
              <small>逐个标签组思考，适合新用户或需要细致整理时使用。</small>
            </span>
          </el-radio>
          <el-radio label="quick" class="mode-setting-card">
            <span class="mode-setting-icon"><i class="el-icon-lightning"></i></span>
            <span>
              <strong>快速模式</strong>
              <small>展开标签组直接选择，适合已经熟悉标签体系的用户。</small>
            </span>
          </el-radio>
        </el-radio-group>
      </section>

      <span slot="footer">
        <el-button @click="localSettingsVisible = false">取消</el-button>
        <el-button type="primary" @click="saveLocalSettings">保存设置</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import apis from "@/api/index";
import {
  getSoundTagPreferences,
  saveSoundTagPreferences
} from "@/lib/soundTagPreferences";

export default {
  name: 'Sidebar',
  props: {
    isCollapse: { type: Boolean, default: false }
  },
  data() {
    return {
      findSoundUnreadQueryCount: 0,
      unreadTimer: null,
      unreadFetchedAt: 0,
      localSettingsVisible: false,
      localSettings: getSoundTagPreferences(),
    };
  },
  watch: {
    '$route.path'() {
      this.syncUnreadPolling(true);
    },
  },
  computed: {
    adminLevel() {
      // 你工程里的 getter；没有就回退 state
      const g = this.$store.getters && this.$store.getters.getAdminLevel
      if (typeof g === 'number') return g
      return (this.$store.state.user && this.$store.state.user.admin_level) || 0
    },

    // 仅维护展示清单；是否显示由 canSeePath 决定
    menuGroups() {
      return [
        {
          title: '监控',
          icon: 'el-icon-data-line',
          items: [
            { label: '在线用户折线图', path: '/index/lineChart' },
            { label: '在线数据占比', path: '/index/dataPersent' }
          ]
        },
        {
          title: '软件版本',
          icon: 'el-icon-collection',
          items: [
            { label: '版本列表', path: '/appversion' },
            { label: '用户问题反馈', path: '/appversion/suggestions' }
          ]
        },
        {
          title: '管理列表',
          icon: 'el-icon-document-checked',
          items: [{ label: '管理列表', path: '/admin' }]
        },
        {
          title: '用户管理',
          icon: 'el-icon-user',
          items: [{ label: '用户列表', path: '/user' }]
        },
        // 2026-10-05：补齐话术管理菜单；通过现有路由权限过滤，仅最高管理员可见。
        {
          title: '客服话术',
          icon: 'el-icon-chat-line-square',
          items: [{ label: '话术与分类', path: '/support/phrases' }]
        },
        {
          title: '资源管理',
          icon: 'el-icon-folder-opened',
          items: [
            { label: '图片资源库', path: '/assets/images' },
            { label: '图片文件管理器', path: '/assets/image-files' }
          ]
        },
        {
          title: '音效分类管理',
          icon: 'el-icon-menu',
          items: [
            // 关键：用具体子路由，别用父级 /sounds_v2（通常会 redirect）
            { label: '音效分类', path: '/sounds_v2/menus' },
            { label: '添加音效分类', path: '/sounds_v2/add_menus' },
            { label: '分类与音效', path: '/sounds_v2/table' }
          ]
        },
        {
          title: '音效管理',
          icon: 'el-icon-video-play',
          items: [
            { label: '音效列表', path: '/sounds' },
            { label: '音效基础分类', path: '/sounds/category' },
            { label: '标签与搜索词', path: '/sounds/tags' },
            { label: '版权与展示控制', path: '/sounds/rights' },
            { label: '资源问题反馈', path: '/sounds/complaints' },
            { label: '搜索关键词分析', path: '/sounds/userSearchLog' },
            { label: '找音效', path: '/sounds/findSound' }
          ]
        },
        {
          title: '代理管理',
          icon: 'el-icon-suitcase',
          items: [{ label: '代理列表', path: '/agents' }]
        },
        {
          title: 'SKU 管理',
          icon: 'el-icon-box',
          items: [{ label: 'SKU 列表', path: '/codetypes' }]
        },
        {
          title: '软件设置',
          icon: 'el-icon-setting',
          items: [
            { label: '通用设置', path: '/app_setting' },
            { label: '试用配置', path: '/app_setting/trial_config' },
            { label: '授权配置', path: '/app_setting/license_config' }
          ]
        },
        {
          title: '会员权限',
          icon: 'el-icon-lock',
          items: [
            { label: '功能权限', path: '/license/features' },
            { label: '会员套餐', path: '/license/plans' },
            { label: '激活码生成', path: '/license/activation-codes' }
          ]
        }
      ]
    },

    // 过滤出可见分组
    visibleMenuGroups() {
      const groups = []
      for (const g of this.menuGroups) {
        const visItems = g.items.filter(it => this.canSeePath(it.path))
        if (visItems.length > 0) groups.push({ ...g, items: visItems })
      }
      return groups
    }
  },
  methods: {
    // 取路由链条的最大 minLevel；未配置按 0；无匹配返回 Infinity（直接隐藏）
    requiredLevel(path) {
      let matched
      try { matched = this.$router.match(path).matched } catch (e) { matched = null }
      if (!matched || !matched.length) return Number.POSITIVE_INFINITY
      let need = 0
      for (let i = 0; i < matched.length; i++) {
        const r = matched[i]
        const v = r && r.meta && typeof r.meta.minLevel === 'number' ? r.meta.minLevel : 0
        if (v > need) need = v
      }
      return need
    },

    // 路由是否声明在侧边栏隐藏（支持 hidden 或 meta.sidebarHidden）
    isHiddenInSidebar(path) {
      let matched
      try { matched = this.$router.match(path).matched } catch (e) { matched = null }
      if (!matched || !matched.length) return true
      for (let i = 0; i < matched.length; i++) {
        const r = matched[i]
        if ((r && r.hidden === true) || (r && r.meta && r.meta.sidebarHidden === true)) {
          return true
        }
      }
      return false
    },

    // 最终是否显示该菜单项
    canSeePath(path) {
      if (this.isHiddenInSidebar(path)) return false
      const need = this.requiredLevel(path)
      return this.adminLevel >= need
    },
    openLocalSettings() {
      this.localSettings = getSoundTagPreferences()
      this.localSettingsVisible = true
    },
    saveLocalSettings() {
      this.localSettings = saveSoundTagPreferences(this.localSettings)
      this.localSettingsVisible = false
      this.$message.success('本机偏好设置已保存')
    },
    loadFindSoundUnreadStat() {
      apis.findSound
        .unreadStat({})
        .then((res) => {
          const data = res && res.data ? res.data : {};
          const stat = data && data.data ? data.data : {};
          const serverFetchedAtMs = Number(stat.server_timestamp_ms || 0);
          // 侧边栏未读也采用“只前进不回退”的时间戳口径，与详情页保持一致。
          this.unreadFetchedAt = serverFetchedAtMs > 0 ? serverFetchedAtMs : Date.now();
          this.findSoundUnreadQueryCount = Number(stat.unread_query_count || 0);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    startUnreadTimer() {
      this.stopUnreadTimer();
      this.unreadTimer = setInterval(() => {
        this.loadFindSoundUnreadStat();
      }, 60000);
    },
    stopUnreadTimer() {
      if (this.unreadTimer) {
        clearInterval(this.unreadTimer);
        this.unreadTimer = null;
      }
    },
    handleFindSoundUnreadStatChanged(event) {
      const payload = event && event.detail ? event.detail : {};
      const incomingFetchedAt = Number(payload.fetched_at || payload.server_timestamp_ms || 0);
      // 忽略旧事件，避免页面并发请求返回顺序不稳定时把新状态覆盖掉。
      if (incomingFetchedAt > 0 && this.unreadFetchedAt > 0 && incomingFetchedAt < this.unreadFetchedAt) {
        return;
      }
      if (incomingFetchedAt > 0) {
        this.unreadFetchedAt = incomingFetchedAt;
      }
      this.findSoundUnreadQueryCount = Number(payload.unread_query_count || 0);
    },
    isFindSoundRouteFocused() {
      const path = (this.$route && this.$route.path) ? String(this.$route.path) : '';
      if (path !== '/sounds/findSound') return false;
      if (typeof document !== 'undefined' && document.hidden) return false;
      // 仅当窗口聚焦时才轮询，避免用户不关注时持续请求。
      if (typeof document !== 'undefined' && typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
      return true;
    },
    syncUnreadPolling(forceLoad = false) {
      if (!this.isFindSoundRouteFocused()) {
        this.stopUnreadTimer();
        return;
      }
      if (forceLoad) {
        this.loadFindSoundUnreadStat();
      }
      this.startUnreadTimer();
    },
    handleVisibilityChange() {
      this.syncUnreadPolling(false);
    },
    handleWindowFocus() {
      this.syncUnreadPolling(true);
    }
  },
  mounted() {
    this.loadFindSoundUnreadStat();
    this.syncUnreadPolling(false);
    window.addEventListener('admin_find_sound_unread_stat_changed', this.handleFindSoundUnreadStatChanged);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.handleWindowFocus);
    }
  },
  beforeDestroy() {
    this.stopUnreadTimer();
    window.removeEventListener('admin_find_sound_unread_stat_changed', this.handleFindSoundUnreadStatChanged);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.handleWindowFocus);
    }
  }
}
</script>

<style lang="scss">
.sidebar-wrapper {
  display: flex;
  height: 100vh;
  flex-direction: column;

  .scrollbar-wrapper { overflow-x: hidden !important; }
  .el-scrollbar__bar.is-vertical { right: 0px; }
  .el-scrollbar { min-height: 0; height: auto; flex: 1; }

  .menu-item-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .menu-item-unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #f56c6c;
    flex: 0 0 auto;
  }
}

.sidebar-footer {
  flex: 0 0 auto;
  padding: 8px 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: #304156;

  &.collapsed { padding-right: 6px; padding-left: 6px; }
}

.sidebar-settings-button {
  display: flex;
  width: 100%;
  min-height: 40px;
  padding: 0 13px;
  align-items: center;
  gap: 11px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #d8e0eb;
  cursor: pointer;
  font-size: 14px;
  text-align: left;
  transition: background 0.16s ease;

  &:hover { background: rgba(255, 255, 255, 0.09); color: #fff; }
  i { font-size: 17px; }
}
</style>

<style lang="scss">
.local-setting-section {
  padding: 4px 2px;
}
.setting-heading {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;

  h3 { margin: 0; color: #28364d; font-size: 16px; }
  p { margin: 6px 0 0; color: #8793a5; font-size: 12px; }
}
.mode-setting-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  width: 100%;
}
.mode-setting-card.el-radio {
  display: flex;
  min-height: 74px;
  margin: 0;
  padding: 14px 15px;
  align-items: flex-start;
  box-sizing: border-box;
  border: 1px solid #dfe5ee;
  border-radius: 10px;
  white-space: normal;

  &.is-checked {
    border-color: #88aaff;
    background: #f2f6ff;
  }
  .el-radio__input { margin-top: 8px; }
  .el-radio__label {
    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 1.45;
  }
  strong { display: block; color: #31405a; font-size: 14px; }
  small { display: block; margin-top: 4px; color: #8793a5; font-size: 12px; }
}
.mode-setting-icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 9px;
  background: #eaf0ff;
  color: #356df3;
  font-size: 18px;
}
</style>
